import { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import Layout from '../common/Layout'
import { Card } from '../common/Card'
import { getLifeEvents } from '../../services/api'

function MyLifeHub() {
  const navigate = useNavigate()
  const location = useLocation()
  const [lifeEvents, setLifeEvents] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // Determine active tab based on URL
  const getActiveTab = () => {
    if (location.pathname === '/milestones') return 'milestones'
    if (location.pathname === '/memories') return 'memories'
    return 'milestones' // default
  }

  const [activeTab, setActiveTab] = useState(getActiveTab())

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true)
        const events = await getLifeEvents()
        setLifeEvents(events)
        setLoading(false)
      } catch (err) {
        console.error('Error fetching life events:', err)
        setError('Failed to load life events')
        setLoading(false)
      }
    }
    fetchData()
  }, [])

  const handleTabChange = (tab) => {
    setActiveTab(tab)
    // Update URL to match tab
    if (tab === 'milestones') {
      navigate('/milestones', { replace: true })
    } else {
      navigate('/memories', { replace: true })
    }
  }

  // Filter events based on active tab
  const filteredEvents = activeTab === 'milestones'
    ? lifeEvents.filter(event => event.template !== 12 && event.category !== 'Stories')
    : lifeEvents.filter(event => event.template === 12 || event.category === 'Stories' || event.event_data?.story_type)

  return (
    <Layout>
      <div className="bg-[#F5F5DC] min-h-screen p-4 sm:p-8">
        {/* Page Title */}
        <h1 className="text-3xl sm:text-4xl font-bold mb-6 text-[#556B2F]">My Life</h1>

        {/* Tab Navigation */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden mb-6">
          <div className="flex border-b border-gray-200">
            <button
              onClick={() => handleTabChange('milestones')}
              className={`flex-1 px-4 sm:px-8 py-3 sm:py-4 text-base sm:text-lg font-medium transition-colors ${
                activeTab === 'milestones'
                  ? 'text-white bg-[#556B2F] border-b-4 border-[#556B2F]'
                  : 'text-[#556B2F] hover:bg-gray-50'
              }`}
            >
              📌 Milestones
            </button>
            <button
              onClick={() => handleTabChange('memories')}
              className={`flex-1 px-4 sm:px-8 py-3 sm:py-4 text-base sm:text-lg font-medium transition-colors ${
                activeTab === 'memories'
                  ? 'text-white bg-[#556B2F] border-b-4 border-[#556B2F]'
                  : 'text-[#556B2F] hover:bg-gray-50'
              }`}
            >
              💭 Memories
            </button>
          </div>
        </div>

        {/* Tab Content */}
        <div className="transition-all duration-300">
          {loading ? (
            <div className="text-center py-8">Loading...</div>
          ) : error ? (
            <div className="text-center py-8 text-red-500">{error}</div>
          ) : (
            <Card className="p-6 bg-white rounded-lg shadow-md">
              <h2 className="text-2xl font-bold mb-4 text-[#556B2F]">
                {activeTab === 'milestones' ? '📌 Life Milestones' : '💭 Cherished Memories'}
              </h2>
              <p className="text-gray-600 mb-6">
                {activeTab === 'milestones'
                  ? 'Important moments and achievements in your life journey'
                  : 'Stories, moments, and experiences you want to remember'}
              </p>

              {/* Events Table */}
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead className="bg-[#556B2F] text-white">
                    <tr>
                      <th className="px-6 py-3 text-left text-sm font-semibold">Date</th>
                      <th className="px-6 py-3 text-left text-sm font-semibold">Event</th>
                      <th className="px-6 py-3 text-left text-sm font-semibold">Category</th>
                      <th className="px-6 py-3 text-left text-sm font-semibold">Location</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredEvents.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="px-6 py-4 text-center text-gray-500">
                          {activeTab === 'milestones'
                            ? 'No milestones recorded yet'
                            : 'No memories shared yet'}
                        </td>
                      </tr>
                    ) : (
                      filteredEvents.map((event, index) => (
                        <tr
                          key={event.id || index}
                          className="hover:bg-[#F5F5DC] transition-colors cursor-pointer"
                        >
                          <td className="px-6 py-4 text-sm">
                            {new Date(event.date).toLocaleDateString()}
                          </td>
                          <td className="px-6 py-4 text-sm font-medium text-gray-900">
                            {event.title}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-500">
                            {event.category}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-500">
                            {event.location || 'N/A'}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Summary Stats */}
              <div className="mt-6 pt-6 border-t border-gray-200">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-center">
                  <div>
                    <div className="text-3xl font-bold text-[#556B2F]">{filteredEvents.length}</div>
                    <div className="text-sm text-gray-600">
                      {activeTab === 'milestones' ? 'Milestones' : 'Memories'}
                    </div>
                  </div>
                  <div>
                    <div className="text-3xl font-bold text-[#556B2F]">{lifeEvents.length}</div>
                    <div className="text-sm text-gray-600">Total Events</div>
                  </div>
                  <div>
                    <div className="text-3xl font-bold text-[#556B2F]">
                      {new Set(filteredEvents.map(e => e.category)).size}
                    </div>
                    <div className="text-sm text-gray-600">Categories</div>
                  </div>
                </div>
              </div>
            </Card>
          )}
        </div>
      </div>
    </Layout>
  )
}

export { MyLifeHub }
